import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Button, Modal, Form, Input, Switch, DatePicker,
  Tag, Space, Tooltip, message, Checkbox, Row, Col, Typography,
  Drawer, Divider, Popconfirm, Steps, Card,
} from "antd";
import {
  PlusOutlined, CopyOutlined, MailOutlined, EditOutlined,
  DeleteOutlined, LinkOutlined, EyeOutlined, PoweroffOutlined,
  ArrowLeftOutlined, ShareAltOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { API_CONST } from "../../../const";

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;

const DEFAULT_VF = {
  name: true, role: true, skills: true, secondarySkills: true,
  experience: true, availability: true, location: true, clientRate: false,
};

const VF_LABELS = {
  name: "Talent Name",
  role: "Role",
  skills: "Primary Skills",
  secondarySkills: "Secondary Skills",
  experience: "Experience",
  availability: "Availability",
  location: "Location",
  clientRate: "Partner Rate (₹/mo)",
};

const BenchPoolShares = ({ benchTalents = [], onBack }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingShare, setEditingShare] = useState(null);
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();
  const [visibleFields, setVisibleFields] = useState({ ...DEFAULT_VF });
  const [selectedTalentIds, setSelectedTalentIds] = useState([]);
  const [allTalents, setAllTalents] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailShare, setEmailShare] = useState(null);
  const [emailForm] = Form.useForm();
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem("authToken");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchShares = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_CONST.BENCH_SHARE_BASE, { headers });
      setShares(res.data.shares || []);
    } catch {
      message.error("Failed to load shares.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShares(); }, [fetchShares]);

  const openCreate = () => {
    setEditingShare(null);
    form.resetFields();
    setVisibleFields({ ...DEFAULT_VF });
    setSelectedTalentIds([]);
    setAllTalents(true);
    setStep(0);
    setModalOpen(true);
  };

  const openEdit = (share) => {
    setEditingShare(share);
    form.setFieldsValue({
      title: share.title,
      message: share.message,
      expiresAt: share.expiresAt ? dayjs(share.expiresAt) : null,
    });
    setVisibleFields({ ...DEFAULT_VF, ...(share.visibleFields || {}) });
    const ids = share.selectedTalentIds || [];
    setAllTalents(ids.length === 0);
    setSelectedTalentIds(ids);
    setStep(0);
    setModalOpen(true);
  };

  const handleNextStep = async () => {
    if (step === 0) {
      try {
        await form.validateFields(['title']);
      } catch { return; }
    }
    setStep(step + 1);
  };

  const handleSave = async () => {
    const values = form.getFieldsValue(true);
    const payload = {
      title: values.title,
      message: values.message || "",
      visibleFields,
      selectedTalentIds: allTalents ? null : selectedTalentIds,
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
    };
    setSaving(true);
    try {
      if (editingShare) {
        await axios.patch(`${API_CONST.BENCH_SHARE_BASE}/${editingShare.id}`, payload, { headers });
        message.success("Share updated.");
      } else {
        await axios.post(API_CONST.BENCH_SHARE_BASE, payload, { headers });
        message.success("Share created.");
      }
      setModalOpen(false);
      fetchShares();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to save share.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (share) => {
    try {
      await axios.patch(`${API_CONST.BENCH_SHARE_BASE}/${share.id}`, { isActive: !share.isActive }, { headers });
      message.success(`Share ${share.isActive ? "deactivated" : "activated"}.`);
      fetchShares();
    } catch {
      message.error("Failed to update share.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_CONST.BENCH_SHARE_BASE}/${id}`, { headers });
      message.success("Share deleted.");
      fetchShares();
    } catch {
      message.error("Failed to delete share.");
    }
  };

  const getShareUrl = (share) =>
    share?.shareSlug
      ? `${window.location.origin}/bench/share/${share.shareSlug}`
      : share?.shareUrl || "";

  const copyLink = (share) => {
    navigator.clipboard.writeText(getShareUrl(share));
    message.success("Link copied!");
  };

  const openEmailModal = (share) => {
    setEmailShare(share);
    emailForm.resetFields();
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    const values = await emailForm.validateFields();
    const emails = values.emails
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) { message.error("Enter at least one email."); return; }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_CONST.BENCH_SHARE_BASE}/${emailShare.id}/send-email`,
        { emails, message: values.message },
        { headers }
      );
      message.success(`Sent to ${res.data.sent} recipient${res.data.sent !== 1 ? "s" : ""}${res.data.failed > 0 ? ` (${res.data.failed} failed)` : ""}.`);
      setEmailModalOpen(false);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, rec) => (
        <div>
          <div style={{ fontWeight: 600, color: "#014c75" }}>{title}</div>
          {rec.message && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: rec.message }}>
              {rec.message.slice(0, 60)}{rec.message.length > 60 ? "…" : ""}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 90,
      render: (_, rec) => {
        const expired = rec.expiresAt && new Date(rec.expiresAt) < new Date();
        if (expired) return <Tag color="default">Expired</Tag>;
        return rec.isActive ? <Tag color="success">Active</Tag> : <Tag color="default">Inactive</Tag>;
      },
    },
    {
      title: "Talents",
      key: "talents",
      width: 80,
      align: "center",
      render: (_, rec) => (
        <Tag>{rec.selectedTalentIds?.length || "All"}</Tag>
      ),
    },
    {
      title: "Views",
      dataIndex: "viewCount",
      key: "viewCount",
      width: 70,
      align: "center",
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "Expires",
      key: "expires",
      width: 110,
      render: (_, rec) => rec.expiresAt
        ? <Text style={{ fontSize: 12 }}>{dayjs(rec.expiresAt).format("DD MMM YYYY")}</Text>
        : <Text type="secondary" style={{ fontSize: 12 }}>Never</Text>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (v) => <Text style={{ fontSize: 12 }}>{dayjs(v).format("DD MMM YYYY")}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title="Copy Link">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyLink(rec)} />
          </Tooltip>
          <Tooltip title="Send Email">
            <Button size="small" icon={<MailOutlined />} onClick={() => openEmailModal(rec)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(rec)} />
          </Tooltip>
          <Tooltip title={rec.isActive ? "Deactivate" : "Activate"}>
            <Button
              size="small"
              icon={<PoweroffOutlined />}
              danger={rec.isActive}
              onClick={() => handleToggleActive(rec)}
            />
          </Tooltip>
          <Popconfirm title="Delete this share?" onConfirm={() => handleDelete(rec.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete">
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const steps = [
    { title: "Details" },
    { title: "Talents" },
    { title: "Visibility" },
    { title: "Expiry" },
  ];

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <>
            <Form.Item name="title" label="Share Title" rules={[{ required: true, message: "Title is required" }]}>
              <Input placeholder="e.g. React Developers Available — May 2026" />
            </Form.Item>
            <Form.Item name="message" label="Intro Message (optional)">
              <TextArea rows={3} placeholder="Add a brief note for the prospect viewing this bench pool…" showCount maxLength={500} />
            </Form.Item>
          </>
        );
      case 1:
        return (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={allTalents}
                onChange={(e) => { setAllTalents(e.target.checked); if (e.target.checked) setSelectedTalentIds([]); }}
              >
                <span style={{ fontWeight: 600 }}>All active talents</span>
                <span style={{ color: "#888", fontSize: 12, marginLeft: 6 }}>(recommended — always shows latest bench)</span>
              </Checkbox>
            </div>
            {!allTalents && (
              <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 6, padding: 12 }}>
                {benchTalents.length === 0 ? (
                  <Text type="secondary">No active talents found.</Text>
                ) : (
                  benchTalents
                    .filter((t) => t.status === "Active" || t.status === "Hired")
                    .map((t) => (
                      <div key={t.key} style={{ marginBottom: 8 }}>
                        <Checkbox
                          checked={selectedTalentIds.includes(t.key)}
                          onChange={(e) => {
                            setSelectedTalentIds(e.target.checked
                              ? [...selectedTalentIds, t.key]
                              : selectedTalentIds.filter((id) => id !== t.key));
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>{t.name}</span>
                          <Tag style={{ marginLeft: 8, fontSize: 11 }}>{t.role}</Tag>
                        </Checkbox>
                      </div>
                    ))
                )}
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <div>
            <Paragraph type="secondary" style={{ marginBottom: 14 }}>
              Choose which fields prospects can see on the public page and in the preview email.
            </Paragraph>
            <Row gutter={[12, 12]}>
              {Object.entries(VF_LABELS).map(([key, label]) => (
                <Col span={12} key={key}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 8,
                      border: visibleFields[key] ? "2px solid #00d9a9" : "1px solid #e0e0e0",
                      background: visibleFields[key] ? "#f0fdf8" : "#fafafa",
                      cursor: key === "name" || key === "role" ? "default" : "pointer",
                    }}
                    onClick={() => {
                      if (key === "name" || key === "role") return;
                      setVisibleFields((p) => ({ ...p, [key]: !p[key] }));
                    }}
                    bodyStyle={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#262626" }}>
                      {label}
                      {key === "clientRate" && (
                        <Tag color="orange" style={{ fontSize: 10, marginLeft: 6 }}>Opt-in</Tag>
                      )}
                    </span>
                    <Switch
                      size="small"
                      checked={visibleFields[key]}
                      disabled={key === "name" || key === "role"}
                      onChange={(checked) => setVisibleFields((p) => ({ ...p, [key]: checked }))}
                      onClick={(_, e) => e.stopPropagation()}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
            <div style={{ marginTop: 12, background: "#fff9e6", padding: "10px 14px", borderRadius: 6, border: "1px solid #faad14" }}>
              <Text style={{ fontSize: 12, color: "#ad6800" }}>
                ⚠️ Settled cost and internal rates are <strong>never</strong> exposed regardless of settings.
              </Text>
            </div>
          </div>
        );
      case 3:
        return (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 14 }}>
              Optionally set an expiry date. After this date the share link will show an "expired" page. Leave blank for no expiry.
            </Paragraph>
            <Form.Item name="expiresAt" label="Expiry Date (optional)">
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(d) => d && d < dayjs().startOf("day")}
                placeholder="No expiry"
              />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} type="text" />
          <div>
            <Title level={4} style={{ margin: 0, color: "#014c75" }}>
              <ShareAltOutlined style={{ marginRight: 8 }} />
              Share Bench Pool
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Create shareable links to your bench pool for prospects and clients
            </Text>
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: "#014c75" }}>
          Create Share
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={shares}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total) => `Total ${total} rows`,
        }}
        locale={{ emptyText: "No share links yet — create one to start sharing your bench pool." }}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editingShare ? "Edit Share" : "Create Share Link"}
        onCancel={() => setModalOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={() => step > 0 ? setStep(step - 1) : setModalOpen(false)}>
              {step === 0 ? "Cancel" : "Back"}
            </Button>
            <Button
              type="primary"
              onClick={step < 3 ? handleNextStep : handleSave}
              loading={saving}
              style={{ background: "#014c75" }}
            >
              {step < 3 ? "Next →" : (editingShare ? "Save Changes" : "Create Share")}
            </Button>
          </div>
        }
        width={580}
      >
        <Steps current={step} size="small" items={steps} style={{ marginBottom: 24 }} />
        <Form form={form} layout="vertical">
          {renderStepContent()}
        </Form>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        open={emailModalOpen}
        title={<><MailOutlined style={{ marginRight: 8 }} />Send Bench Pool by Email</>}
        onCancel={() => setEmailModalOpen(false)}
        onOk={handleSendEmail}
        okText="Send Email"
        confirmLoading={sending}
        okButtonProps={{ style: { background: "#014c75" } }}
        width={520}
      >
        {emailShare && (
          <div style={{ background: "#f0f5ff", padding: "10px 14px", borderRadius: 6, marginBottom: 16 }}>
            <Text style={{ fontSize: 13 }}>
              Sending: <strong>{emailShare.title}</strong>
            </Text>
            <div style={{ marginTop: 4 }}>
              <a href={getShareUrl(emailShare)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#014c75" }}>
                <LinkOutlined style={{ marginRight: 4 }} />{getShareUrl(emailShare)}
              </a>
            </div>
          </div>
        )}
        <Form form={emailForm} layout="vertical">
          <Form.Item name="emails" label="To (one per line, or comma-separated)" rules={[{ required: true, message: "Enter at least one email" }]}>
            <TextArea rows={4} placeholder={"john@acme.com\njane@corp.io"} />
          </Form.Item>
          <Form.Item name="message" label="Additional message (optional)">
            <TextArea rows={2} placeholder="Add a personal note to include in the email…" showCount maxLength={300} />
          </Form.Item>
          <div style={{ background: "#f9f9f9", padding: "10px 14px", borderRadius: 6, fontSize: 12, color: "#666" }}>
            <EyeOutlined style={{ marginRight: 6 }} />
            The email includes a preview table of up to 5 talents and a "View Full Bench Pool" button.
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BenchPoolShares;
